import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const PACKS = [
  { id: "credits_5", credits: 5, price: "£2.99" },
  { id: "credits_10", credits: 10, price: "£4.99", best: true },
];
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "long" }) : "the 1st");

// Confirms a returning credits checkout (?credits_session=…).
const useCreditsReturn = () => {
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("credits_session");
    if (!sid) return undefined;
    url.searchParams.delete("credits_session");
    window.history.replaceState({}, "", url.toString());
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/api/voice-clone/checkout/status/${sid}`, auth());
        if (r.data.payment_status === "paid") { clearInterval(t); toast.success("Your credits have been added"); }
      } catch { /* keep polling */ }
      if (n >= 15) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, []);
};

// Opens whenever any request hits the monthly own-voice cap.
export const CreditsSheet = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  useCreditsReturn();
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (err) => {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d?.code === "clone_cap_reached") setInfo(d);
      // Free members: anything that would create something personal goes to the guided demo's join step.
      if (err?.response?.status === 402 && (d === "membership_required" || d === "signature_limit") && !window.location.pathname.startsWith("/soulumo/demo")
          && !(d === "signature_limit" && window.location.pathname.startsWith("/signature-installations"))) {
        window.location.assign(`/soulumo/demo?step=pay&reason=${d}`);
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);
  if (!info) return null;
  const buy = async (pack) => {
    setBusy(pack);
    try {
      const r = await axios.post(`${API}/api/voice-clone/credits/checkout`,
        { pack, origin_url: window.location.origin, return_path: window.location.pathname }, auth());
      window.location.href = r.data.checkout_url;
    } catch { setBusy(""); toast.error("We couldn't open checkout. Please try again."); }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(6,10,20,0.8)", backdropFilter: "blur(14px)" }} data-testid="credits-sheet">
      <div className="w-full max-w-md rounded-3xl p-7 relative" style={{ background: "radial-gradient(ellipse at 50% 0%, #1a3058 0%, #0d1f3a 60%, #091629 100%)", border: `1px solid ${TOK.goldMist}` }}>
        <button type="button" onClick={() => setInfo(null)} aria-label="Close" data-testid="credits-sheet-close"
          className="absolute right-4 top-4 w-9 h-9 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}>
          <X size={15} />
        </button>
        <h2 className="text-3xl font-light pr-10" style={{ fontFamily: SERIF, color: TOK.cream }}>You've made {info.limit} this month</h2>
        <p className="mt-2 text-base italic" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid="credits-sheet-reset">
          Your next {info.limit} arrive on {fmtDay(info.resets_on)}. Or add credits to keep creating now.
        </p>
        <div className="mt-6 space-y-3">
          {PACKS.map((p) => (
            <button key={p.id} type="button" disabled={!!busy} onClick={() => buy(p.id)} data-testid={`credits-pack-${p.credits}`}
              className="w-full rounded-2xl p-4 flex items-center justify-between transition-transform duration-200 active:scale-[0.99] disabled:opacity-60"
              style={{ border: `1px solid ${p.best ? TOK.gold : TOK.goldMist}`, background: p.best ? "rgba(215,187,122,0.1)" : "transparent" }}>
              <span className="text-left">
                <span className="block text-lg" style={{ color: TOK.cream, fontFamily: SERIF }}>{p.credits} credits</span>
                <span className="block text-xs" style={{ color: TOK.creamMist, fontFamily: SANS }}>{p.best ? "Best value · never expire" : "Never expire"}</span>
              </span>
              <span className="inline-flex items-center gap-2 text-sm" style={{ color: TOK.gold, fontFamily: SANS }}>
                {busy === p.id && <Loader2 size={14} className="animate-spin" />}{p.price}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-4 text-center text-xs" style={{ color: TOK.creamMist, fontFamily: SANS }}>1 credit = 1 installation in your voice</p>
      </div>
    </div>
  );
};
